import { BusinessRole } from "@1games-ru/region-shared/dist/enums/business/role";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import formatPrice from "@/core/helpers/formatPrice";
import { handleNumericChange } from "@/core/helpers/handleNumericChange";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RubleIcon } from "@/shared/icons";
import { Button, Checkbox, Modal, TextInput } from "@/shared/ui";
import Loader from "@/shared/ui/Loader";
import OfferPlayerInfo from "@/shared/widgets/OfferPlayerInfo";
import OfferView from "@/shared/widgets/OfferView";
import { offerCreatorSelector } from "@/domains/offer/slice";
import getBusinessImage from "@/domains/business/helpers/getBusinessImage";
import useOfferCreatorBusiness from "@/domains/offer/hooks/useOfferCreatorBusiness";
import OfferEstateLocation from "@/domains/offer/components/estate/OfferEstateLocation";

export default function OfferEstateBusinessCreatorContainer({ isShow }: { isShow: boolean }) {
  const { t } = useTranslation("offer");

  const offerCreator = useAppSelector(offerCreatorSelector);

  const [price, setPrice] = useState("");
  const [isAgree, setIsAgree] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const { business, create, close, pending, loading } = useOfferCreatorBusiness(isShow);
  const businessList = useMemo(() => {
    if (business?.length > 0) {
      return business.filter((item) => item.role === BusinessRole.Owner);
    }
    return [];
  }, [business]);

  const currentBusiness = businessList[activeIndex];

  const level =
    businessList.length > 0 ? t(`business:business.level.${currentBusiness.level}`) : "";

  const src = businessList.length > 0 ? getBusinessImage(currentBusiness?.id) : "";
  const srcLogo = businessList.length > 0 ? getBusinessImage(currentBusiness?.id, "logo") : "";

  const address =
    businessList.length > 0
      ? `${t("offer.saintPetersburg")}, ${currentBusiness?.address?.streetName} ${currentBusiness?.address?.index}`
      : "";

  const onSubmit = useCallback(() => {
    return create(currentBusiness?.id, offerCreator?.targetCharacter?.id, Number(price));
  }, [create, currentBusiness?.id, offerCreator?.targetCharacter?.id, price]);

  const toggleAgree = useCallback(() => {
    setIsAgree((state) => !state);
  }, []);

  const handleInputChange = (value: string) => handleNumericChange(value, setPrice);

  const isDisabled = useMemo(() => !price || !isAgree, [price, isAgree]);
  const notEmpty = !pending && !loading && businessList?.length > 0;

  useEffect(() => {
    if (!isShow) {
      setActiveIndex(0);
      setPrice("");
      setIsAgree(false);
    }
  }, [isShow]);

  const footer = (
    <Button
      className={clsx("h-11 mr-1", !notEmpty && "!opacity-0 pointer-events-none")}
      onClick={onSubmit}
      disabled={isDisabled}
      loading={pending}
    >
      {t("offer.accept")}
    </Button>
  );

  const leftText = (
    <div className="flex items-center gap-2.5">
      <RubleIcon className="size-4.5" />
      <div className="flex flex-col gap-0.5">
        <p className="text-14 font-bold">{formatPrice(currentBusiness?.price)} ₽</p>
        <p className="text-11 font-medium uppercase">{t("offer.estate.info.price")}</p>
      </div>
    </div>
  );

  const rightText = (
    <div className="flex flex-col gap-0.5 items-end ml-auto">
      <p className="text-14 font-bold">#{currentBusiness?.id}</p>
      <p className="text-11 font-medium uppercase">{t("offer.business.creator.info.businessId")}</p>
    </div>
  );

  return (
    <Modal
      open={isShow}
      title={t("offer.business.trade.creator.title")}
      onClose={close}
      footer={footer}
      className="w-[30rem] pt-6.5 pb-7.5 pl-7.5 pr-6.5"
      classNameCloseBtn="top-6.5 right-7.5"
      dataMzAccent="lightBlue"
      style={{ fontFamily: "TT Norms Pro", boxShadow: "0px 0px 200px 0px rgba(0, 121, 194, 0.30)" }}
    >
      <div className="relative flex flex-col gap-4 w-full min-h-[39.5rem]">
        {!notEmpty || pending || loading ? (
          <div className="absolute top-0 left-0 z-3 flex justify-center items-center size-full text-16 font-bold text-white/50 text-center leading-none uppercase">
            {!pending && !loading && t("offer.empty")}
            {(pending || loading) && <Loader />}
          </div>
        ) : (
          <>
            <OfferPlayerInfo
              name={offerCreator?.targetCharacter?.name}
              avatar={offerCreator?.targetCharacter?.avatar}
              id={offerCreator?.targetCharacter?.id}
              text={t("offer.buyer")}
            />
            {businessList && (
              <>
                <div>
                  <div className="mb-3 text-inverted text-16 font-medium">
                    {t("offer.business.trade.creator.business")}:
                  </div>
                  <OfferView
                    src={src}
                    level={level}
                    list={businessList || []}
                    activeIndex={activeIndex}
                    setActiveIndex={setActiveIndex}
                    leftText={leftText}
                    rightText={rightText}
                  />
                </div>
                <OfferEstateLocation address={address} />
                <div className="flex justify-between items-center pt-5 pb-1 border-t border-[#191919]">
                  <div className="flex flex-col gap-2">
                    <p className="text-16 font-bold leading-none">{currentBusiness?.name}</p>
                    <p className="text-15 font-medium text-white/50 leading-none">
                      {t("offer.business.trade.creator.info.name")}
                    </p>
                  </div>
                  <div
                    className="relative flex w-[6.6875rem] h-6"
                    style={{
                      backgroundImage: `url(${srcLogo})`,
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                      backgroundSize: "contain",
                    }}
                  />
                </div>
              </>
            )}
            <div>
              <TextInput
                icon="₽"
                title={t("offer.price")}
                placeholder={t("offer.pricePlaceholder")}
                value={price}
                onChange={handleInputChange}
                maxLength={11}
                isIconRight
              />
            </div>
            <div className="flex items-center gap-2.5">
              <Checkbox checked={isAgree} onChange={toggleAgree} />
              <button
                type="button"
                onClick={toggleAgree}
                className="text-14 font-medium text-inverted"
              >
                {t("offer.business.trade.creator.agree")}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
